import { Menu, Transition } from '@headlessui/react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Fragment, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  buildQuantityOptions,
  canUpdateQuantity,
  clampQuantity,
  MAX_DROPDOWN_QUANTITY,
  offersCustomQuantity,
  parseQuantity,
  usesCustomField,
} from './quantity';
import i18n from '@/settings/i18n';

const t = i18n.init('cart');

const FIELD_CLASSNAME =
  'flex h-8 shrink-0 items-center justify-between gap-1 rounded-lg border border-[#E6E7EB] px-2 text-xs';

const QuantitySelector = ({
  quantity,
  max,
  disabled = false,
  onChange,
}: {
  quantity: number;
  max: number;
  disabled?: boolean;
  onChange: (quantity: number) => void | Promise<void>;
}) => {
  const options = buildQuantityOptions(max);

  // A line already at or above the `6+` threshold can only be edited as text,
  // so it opens straight into the field.
  const [isCustom, setIsCustom] = useState(usesCustomField(quantity, max));

  // Updating the cart is a round trip, so `quantity` lags behind the pick by as
  // long as the request takes. Showing the chosen value immediately keeps the
  // field from displaying the old quantity in the meantime.
  const [pending, setPending] = useState<number | null>(null);
  const current = pending ?? quantity;

  const [draft, setDraft] = useState(String(quantity));
  const inputRef = useRef<HTMLInputElement>(null);
  const shouldFocusRef = useRef(false);

  useEffect(() => {
    setDraft(String(current));
  }, [current]);

  // Once the round trip finishes, defer to whatever the cart actually holds —
  // including when it comes back unchanged because the update failed.
  useEffect(() => {
    if (!disabled) {
      setPending(null);
    }
  }, [quantity, disabled]);

  /**
   * Shows the picked quantity straight away, then hands back to the cart once
   * the change has run its course. Tied to the call rather than to `quantity`
   * changing, because a change can legitimately end with the cart untouched —
   * a split the shopper cancelled, or an update that failed — and the field
   * has to return to the real quantity rather than sit on one that never
   * happened.
   */
  const commit = (next: number) => {
    setPending(next);

    Promise.resolve(onChange(next))
      .catch(() => undefined)
      .finally(() => setPending(null));
  };

  // Only focus when the shopper opened the field themselves. A line that merely
  // starts out above the threshold renders the field on mount, and focusing
  // there would steal the caret on page load — the last line item to mount
  // would win it.
  useEffect(() => {
    if (!isCustom || !shouldFocusRef.current) {
      return;
    }

    shouldFocusRef.current = false;

    inputRef.current?.focus();
    inputRef.current?.select();
  }, [isCustom]);

  const resetDraft = () => {
    setDraft(String(current));
    setIsCustom(usesCustomField(current, max));
  };

  const commitDraft = () => {
    const parsed = parseQuantity(draft);

    if (parsed === null) {
      resetDraft();

      return;
    }

    const next = clampQuantity(parsed, max);

    // Show the clamped value even when it matches the quantity already in the
    // cart, so an over-max entry visibly settles instead of lingering.
    setDraft(String(next));
    setIsCustom(usesCustomField(next, max));

    if (next !== current) {
      commit(next);
    }
  };

  // `6+` opens the field pre-filled at 6 — the lowest quantity it stands for.
  const openCustomField = () => {
    shouldFocusRef.current = true;
    setDraft(String(MAX_DROPDOWN_QUANTITY));
    setIsCustom(true);
  };

  const isUpdateDisabled = disabled || !canUpdateQuantity(draft, current);

  if (isCustom) {
    return (
      <div className="flex items-center gap-1.5">
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={draft}
          disabled={disabled}
          aria-label={t`Quantity`}
          onChange={(event) =>
            setDraft(event.target.value.replace(/[^0-9]/g, ''))
          }
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();

              if (!isUpdateDisabled) {
                commitDraft();
              }
            }

            if (event.key === 'Escape') {
              resetDraft();
            }
          }}
          className={classNames(
            FIELD_CLASSNAME,
            'w-14 text-center focus:border-gray-700 focus:outline-none',
            { 'cursor-not-allowed text-[#99A1AF]': disabled },
          )}
        />
        <button
          type="button"
          onClick={commitDraft}
          disabled={isUpdateDisabled}
          className={classNames(
            'flex h-8 items-center rounded-lg bg-gray-700 px-3 text-xs font-bold text-white duration-200 ease-in-out',
            isUpdateDisabled
              ? 'cursor-not-allowed opacity-70'
              : 'hover:opacity-90',
          )}
        >
          {t`Update`}
        </button>
      </div>
    );
  }

  return (
    <Menu as="div" className="relative">
      {({ open }) => (
        <>
          <Menu.Button
            disabled={disabled}
            className={classNames(
              FIELD_CLASSNAME,
              'min-w-[72px] whitespace-nowrap focus:outline-none',
              {
                'cursor-not-allowed text-[#99A1AF]': disabled,
              },
            )}
          >
            <span>
              {t`Qty`} {current}
            </span>
            {open ? (
              <ChevronUp className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
            ) : (
              <ChevronDown className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
            )}
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 z-[70] mt-1 min-w-full origin-top-right overflow-hidden rounded-lg border border-[#E6E7EB] bg-white text-left shadow focus:outline-none">
              {options.map((option) => (
                <Menu.Item key={option}>
                  {({ active }) => (
                    <button
                      type="button"
                      onClick={() => {
                        if (option !== current) {
                          commit(option);
                        }
                      }}
                      className={classNames(
                        'flex w-full items-center px-3 py-1.5 text-left text-xs',
                        {
                          'bg-gray-700/5': active,
                          'font-semibold': option === current,
                        },
                      )}
                    >
                      {option}
                    </button>
                  )}
                </Menu.Item>
              ))}

              {offersCustomQuantity(max) && (
                <Menu.Item>
                  {({ active }) => (
                    <button
                      type="button"
                      onClick={openCustomField}
                      className={classNames(
                        'flex w-full items-center border-t border-[#E6E7EB] px-3 py-1.5 text-left text-xs',
                        { 'bg-gray-700/5': active },
                      )}
                    >
                      {MAX_DROPDOWN_QUANTITY}+
                    </button>
                  )}
                </Menu.Item>
              )}
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};

export default QuantitySelector;
